import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Bell, BellRing, CheckCheck, Settings } from "lucide-react";
import { Popover } from "radix-ui";
import { useState } from "react";

import { api } from "@/api/client";
import { buttonClass, Skeleton, Tooltip } from "@/components/ui";
import { cn } from "@/lib/cn";

import { useAlertCenter, useInboxSummary } from "./center";
import { KindIcon } from "./AlertCard";
import { countText, INBOX_KEY, whenText, type AlertItem } from "./inbox";

type Tab = "unread" | "all" | "price" | "ideas" | "strategies" | "radar" | "calls" | "ai";

function Item({ item, onOpen }: { item: AlertItem; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full gap-3 px-4 py-3 text-left hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
      >
        <KindIcon item={item} className="size-8 [&_svg]:size-4" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-fg">
            {item.title}
          </span>
          {item.body ? (
            <span className="block truncate text-xs text-muted">
              {item.body}
            </span>
          ) : null}
          {item.why ? (
            <span className={cn("block truncate text-xs", item.why.found ? "text-fg" : "text-subtle")}>
              {item.why.text}
            </span>
          ) : null}
          <span className="mt-1 block text-2xs text-subtle">
            {whenText(item.created_at)}
          </span>
        </span>
        {item.read ? (
          <span className="w-2" />
        ) : (
          <span
            aria-label="unread"
            className="mt-1.5 size-2 shrink-0 rounded-full bg-brand"
          />
        )}
      </button>
    </li>
  );
}

function List({ tab, onOpen }: { tab: Tab; onOpen: (i: AlertItem) => void }) {
  const query = {
    limit: 8,
    days: 0,
    ...(tab === "unread" ? { show: "unread" as const } : {}),
    ...(["price", "ideas", "strategies", "radar", "calls", "ai"].includes(tab)
      ? { group: tab as "price" }
      : {}),
  };
  const q = useQuery({
    queryKey: [...INBOX_KEY, "bell", tab],
    queryFn: ({ signal }) => api.get("/me/inbox", { query, signal }),
    staleTime: 10_000,
  });
  if (q.isPending)
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
      </div>
    );
  const items = q.data?.items ?? [];
  if (!items.length)
    return (
      <div className="px-4 py-10 text-center">
        <BellRing aria-hidden className="mx-auto size-6 text-subtle" />
        <p className="mt-2 text-sm font-medium text-fg">
          {tab === "unread" ? "You're all caught up" : "No alerts here yet"}
        </p>
        <p className="mt-0.5 text-xs text-muted">
          New alerts show here and as a card on screen.
        </p>
      </div>
    );
  return (
    <ul className="max-h-[min(26rem,60dvh)] divide-y divide-border overflow-y-auto">
      {items.map((i) => (
        <Item key={i.id} item={i} onOpen={() => onOpen(i)} />
      ))}
    </ul>
  );
}

/** The top bar's bell: the unread count, and the latest alerts in a dropdown. */
export function AlertBell() {
  const center = useAlertCenter();
  const summary = useInboxSummary().data;
  const [tab, setTab] = useState<Tab>("unread");
  if (!center.enabled) return null;
  const n = summary?.unread ?? 0;
  const tabs: { key: Tab; label: string }[] = [
    { key: "unread", label: "Unread" },
    { key: "all", label: "All" },
    { key: "price", label: "Price" },
    { key: "ideas", label: "Ideas" },
    { key: "strategies", label: "Strategies" },
    { key: "radar", label: "Radar" },
    // calls from the member's Research Analysts (V063), once there are any
    ...(summary?.by_group?.calls || summary?.unseen?.some((i) => i.kind === "ra_call")
      ? [{ key: "calls" as Tab, label: "Calls" }]
      : []),
    ...(summary?.friend ? [{ key: "ai" as Tab, label: "AI" }] : []),
  ];
  const label = n ? `Alerts, ${n} unread` : "Alerts";
  return (
    <Popover.Root open={center.bellOpen} onOpenChange={center.setBellOpen}>
      <Tooltip content={label}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(buttonClass("ghost", "icon"), "relative")}
          >
            <Bell />
            {n ? (
              <span
                aria-hidden
                className="absolute top-1 right-1 grid h-4 min-w-4 place-items-center rounded-full bg-down px-1 text-[10px] leading-none font-bold text-white tabular-nums"
              >
                {countText(n)}
              </span>
            ) : null}
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          aria-label="Alerts"
          className="z-50 w-[min(26rem,calc(100vw-24px))] overflow-hidden rounded-xl border border-border bg-surface shadow-[0_16px_40px_-12px_rgb(15_23_42/0.35)]"
        >
          <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
            <h2 className="text-base font-semibold text-fg">Alerts</h2>
            {n ? (
              <span className="rounded-md bg-brand-soft px-1.5 py-0.5 text-2xs font-semibold text-brand">
                {n} unread
              </span>
            ) : null}
            <button
              type="button"
              disabled={!n}
              onClick={() => center.readAll()}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-muted hover:bg-surface-2 hover:text-fg disabled:opacity-50"
            >
              <CheckCheck className="size-3.5" />
              Mark all read
            </button>
            <Link
              to="/app/account"
              hash="notifications"
              aria-label="Notification settings"
              onClick={() => center.setBellOpen(false)}
              className="rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-fg"
            >
              <Settings className="size-4" />
            </Link>
          </div>
          <div className="px-4 pb-2">
            <div
              role="tablist"
              aria-label="Show"
              className="flex max-w-full gap-0.5 overflow-x-auto rounded-lg bg-surface-2 p-0.5"
            >
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium whitespace-nowrap text-muted hover:text-fg",
                    tab === t.key && "bg-surface text-fg shadow-card",
                  )}
                >
                  {t.label}
                  {t.key === "unread" && n ? (
                    <span className="grid h-4 min-w-4 place-items-center rounded-full bg-brand px-1 text-[10px] font-bold text-brand-fg tabular-nums">
                      {countText(n)}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-border">
            <List tab={tab} onOpen={(i) => center.open(i)} />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-2/60 px-4 py-2.5">
            <Link
              to="/app/alerts"
              onClick={() => center.setBellOpen(false)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
            >
              Open alerts history
              <ArrowRight className="size-3.5" />
            </Link>
            <span className="text-2xs text-subtle">Kept for 12 months</span>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The count next to "Alerts" in the menu: the same number as the bell. */
export function AlertsBadge() {
  const n = useInboxSummary().data?.unread ?? 0;
  if (n <= 0) return null;
  return (
    <span
      className="ml-auto rounded-full bg-brand px-1.5 text-2xs leading-4 font-semibold text-brand-fg tabular-nums"
      aria-label={`${n} unread alert${n === 1 ? "" : "s"}`}
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}
